import { Fragment, type JSX } from 'react';
import { trackAbTestV2Variant } from 'dibs-ab-tests/exports/clientAbTestV2.ts';
import { EVERGREEN_COLLECTIONS_TEST_NAME } from './helpers/evergreenCollectionsTestHelpers.ts';

import { VisibilityTracker } from 'dibs-visibility-tracker/exports/VisibilityTracker.tsx';
import { HpSharedLocationsModule } from './HpSharedLocationsModule/HpSharedLocationsModule.tsx';
import { HpSharedTrendingNowCarouselTabs } from './HpSharedTrendingNowCarouselTabs/HpSharedTrendingNowCarouselTabs.tsx';
import HpSharedPromoModule from './HpSharedPromoModule/HpSharedPromoModule.tsx';
import { HpSharedCollectionsModule } from './HpSharedCollectionsModule/HpSharedCollectionsModule.tsx';
import { HpSharedShopByModule } from './HpSharedShopByModule/HpSharedShopByModule.tsx';
import { HpSharedTopHeroBannerModule } from './HpSharedTopHeroBannerModule/HpSharedTopHeroBannerModule.tsx';
import { HpSharedEditorialModule } from './HpSharedEditorialModule/HpSharedEditorialModule.tsx';
import { HpSharedFeaturedBannerModule } from './HpSharedFeaturedBannerModule/HpSharedFeaturedBannerModule.tsx';
import HpSharedVideoComponent from './HpSharedVideoComponent/HpSharedVideoComponent.tsx';

import { type HpSharedLayout_viewer$data } from './__generated__/HpSharedLayout_viewer.graphql.ts';

type Homepage = NonNullable<HpSharedLayout_viewer$data['homepage']>;
type User = HpSharedLayout_viewer$data['user'];
type HomepageModules = NonNullable<NonNullable<Homepage['modules']>[0]>;

type Props = {
    componentModule: HomepageModules;
    key: string;
    startingPromoIndex: number;
    viewer: HpSharedLayout_viewer$data;
    isMobile: boolean;
    useLazyLoadImages: boolean;
    moduleIndex: number;
    totalModules: number;
    user?: User;
    isClient: boolean;
    isEvergreenCollectionsTestVariant: boolean;
    editorialModuleOrder: number;
};

export const componentModuleMap: Record<string, (payload: Props) => JSX.Element | null> = {
    topHeroBanner: ({ key, componentModule, isMobile }) => (
        <HpSharedTopHeroBannerModule
            key={key}
            componentModule={componentModule}
            isMobile={isMobile}
        />
    ),
    collections: ({ componentModule, key, useLazyLoadImages, moduleIndex, totalModules }) => (
        <HpSharedCollectionsModule
            key={key}
            componentModule={componentModule}
            useLazyLoadImages={useLazyLoadImages}
            moduleIndex={moduleIndex}
            totalModules={totalModules}
        />
    ),
    promo: ({
        componentModule,
        startingPromoIndex,
        key,
        useLazyLoadImages,
        isMobile,
        moduleIndex,
        totalModules,
    }) => (
        <HpSharedPromoModule
            key={key}
            componentModule={componentModule}
            startingPromoIndex={startingPromoIndex}
            useLazyLoadImages={useLazyLoadImages}
            isMobile={isMobile}
            moduleIndex={moduleIndex}
            totalModules={totalModules}
        />
    ),
    editorial: ({ key, componentModule, isMobile, editorialModuleOrder }) => (
        <HpSharedEditorialModule
            key={key}
            componentModule={componentModule}
            isMobile={isMobile}
            editorialModuleOrder={editorialModuleOrder}
        />
    ),
    shopBy: ({ componentModule, key, useLazyLoadImages, moduleIndex, totalModules, isClient }) =>
        isClient ? (
            <HpSharedShopByModule
                key={key}
                componentModule={componentModule}
                useLazyLoadImages={useLazyLoadImages}
                moduleIndex={moduleIndex}
                totalModules={totalModules}
            />
        ) : null,
    locations: ({ key, isMobile, user, viewer, isClient, isEvergreenCollectionsTestVariant }) => {
        const bottomBannerModule = viewer.homepage?.modules?.find(
            mod => mod?.type === 'bottomBanner' && mod?.visible !== false
        );
        return (
            <Fragment key={key}>
                {isClient && (
                    <VisibilityTracker
                        onVisibilityChange={({ isVisible }) => {
                            if (isVisible) {
                                trackAbTestV2Variant(EVERGREEN_COLLECTIONS_TEST_NAME);
                            }
                        }}
                    />
                )}
                {isEvergreenCollectionsTestVariant ? (
                    <HpSharedTrendingNowCarouselTabs
                        isMobile={isMobile}
                        viewer={isClient ? viewer : null}
                        user={user}
                    />
                ) : (
                    <HpSharedLocationsModule
                        isMobile={isMobile}
                        viewer={isClient ? viewer : null}
                        user={user}
                    />
                )}
                {bottomBannerModule && (
                    <HpSharedFeaturedBannerModule
                        componentModule={bottomBannerModule}
                        isMobile={isMobile}
                    />
                )}
            </Fragment>
        );
    },
    video: ({ key, componentModule, user, isClient }) =>
        isClient ? (
            <HpSharedVideoComponent
                key={key}
                user={user}
                fetchFavorites={false}
                componentModule={componentModule}
            />
        ) : null,
};
